<template>
  <div>
    <v-menu>
      <template v-slot:activator="{ props }">
        <MenuActivator v-bind="props" data-cy="apiTableActionMenu" />
      </template>
      <v-list>
        <!-- Data Services Unregister -->
        <v-list-item
          :disabled="removeLock"
          @click.stop="openUnregisterDialog(false)"
          link
          data-cy="apiTableApiUnregisterAction"
          prepend-icon="mdi-map-marker-remove"
        >
          <v-list-item-title>
            {{ $t('apiServices.table.unregisterDataServices') }}
          </v-list-item-title>
        </v-list-item>
        <!-- TSI Link Unregister -->
        <v-list-item
          v-if="!!websiteSubscription"
          :disabled="removeLock"
          @click.stop="openUnregisterDialog(true)"
          link
          data-cy="apiTableWebsiteUnregisterAction"
        >
          <template v-slot:prepend>
            <v-icon v-if="!removeLock" icon="mdi-map-marker-remove" />
            <v-progress-circular
              v-else
              :size="20"
              indeterminate
              color="primary"
              class="action-item-loading"
            />
          </template>
          <v-list-item-title>
            {{ $t('apiServices.table.unregisterTSILink') }}
          </v-list-item-title>
        </v-list-item>
        <!-- TSI Link Register -->
        <v-tooltip location="left" v-else :disabled="!disableWebsiteRegister()">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              :disabled="removeLock"
              @click.stop="openRegisterDialog()"
              link
              data-cy="apiTableWebsiteRegisterAction"
              :class="{
                restrictedContent: disableWebsiteRegister()
              }"
              prepend-icon="mdi-map-marker-plus"
            >
              <v-list-item-title>
                {{ $t('apiServices.table.registerTSILink') }}
              </v-list-item-title>
            </v-list-item>
          </template>
          <span>
            {{ $t('apiServices.tooltipUpgrade') }}
          </span>
        </v-tooltip>
      </v-list>
    </v-menu>
    <!-- Dialogs -->
    <DeviceUnregisterDialog
      :open="dialogs.deviceUnregisterDialog.open"
      :deviceId="dialogs.deviceUnregisterDialog.deviceId"
      :useWebsite="dialogs.deviceUnregisterDialog.useWebsite"
      @unregister="unregisterEvent"
      @close="closeUnregisterDialog"
    />
    <DeviceRegisterDialog
      :open="dialogs.registerDeviceDialog.open"
      :deviceName="dialogs.registerDeviceDialog.deviceName"
      :deviceModel="dialogs.registerDeviceDialog.deviceType"
      :deviceSerial="dialogs.registerDeviceDialog.idSerialNumber"
      :deviceId="dialogs.registerDeviceDialog.deviceId"
      @close="dialogs.registerDeviceDialog.open = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { SUBSCRIPTIONS_GETTERS } from '@/store/subscriptions'
import MenuActivator from '@/components/common/buttons/MenuActivator.vue'
import DeviceUnregisterDialog from '../deviceUnregister/DeviceUnregisterDialog.vue'
import DeviceRegisterDialog from '../registerDevice/registerDialog.vue'

interface RegisterDeviceDialogState {
  open: boolean
  deviceType: string
  idSerialNumber: string
  deviceId?: string
  deviceName?: string
}

interface DeviceUnregisterDialogState {
  open: boolean
  deviceId: string
  useWebsite: boolean
}

export default defineComponent({
  name: 'ApiActionMenu',
  components: {
    DeviceRegisterDialog,
    DeviceUnregisterDialog,
    MenuActivator
  },
  props: {
    deviceId: String,
    websiteSubscription: String,
    removeLock: Boolean
  },
  data() {
    return {
      dialogs: {
        registerDeviceDialog: {
          open: false,
          deviceType: '',
          idSerialNumber: ''
        } as RegisterDeviceDialogState,
        deviceUnregisterDialog: {
          open: false,
          deviceId: '',
          useWebsite: true
        } as DeviceUnregisterDialogState
      }
    }
  },
  methods: {
    openUnregisterDialog(useWebsite: boolean) {
      this.dialogs.deviceUnregisterDialog.deviceId = this.deviceId as string
      this.dialogs.deviceUnregisterDialog.useWebsite = useWebsite
      this.dialogs.deviceUnregisterDialog.open = true
    },
    closeUnregisterDialog() {
      this.dialogs.deviceUnregisterDialog.open = false
    },
    unregisterEvent() {
      // for now, nothing to do
    },
    openRegisterDialog() {
      // If user has remaining subscriptions open register modal
      if (!this.disableWebsiteRegister()) {
        this.dialogs.registerDeviceDialog.deviceId = this.deviceId
        this.dialogs.registerDeviceDialog.idSerialNumber = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`
        ](this.deviceId)
        const deviceModel = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
        ](this.deviceId)
        this.dialogs.registerDeviceDialog.deviceType =
          this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_NAME}`
          ](deviceModel)
        this.dialogs.registerDeviceDialog.open = true
      }
    },
    disableWebsiteRegister(): boolean {
      const model = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ](this.deviceId)
      const canAddToWebsite =
        this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_WEBSITE_BY_MODEL}`
        ](model)
      const hasQtyRemaining =
        this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.HAS_QTY_REMAINING}`
        ]
      // Allow website allowances for devices with corresponding subscription type
      return !hasQtyRemaining && !canAddToWebsite
    }
  }
})
</script>

<style lang="scss" scoped>
.action-item-loading {
  margin-right: 12px;
  margin-left: 4px;
}

.restrictedContent {
  cursor: not-allowed;
  opacity: 0.36;
}
</style>
